<template>
  <div class="tinder-like-step">
    <div class="tinder-survey-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="isLoadingBatch && cards.length === 0" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Загрузка образов...</p>
      </div>

      <!-- Card Container -->
      <FlashCards
        v-else-if="cards.length > 0"
        class="cards full-height"
        :items="cards"
        @approve="onApprove"
        @reject="onReject"
      >
        <template #default="{ item }">
          <TinderLikeCard :item="item" />
        </template>

        <template #actions="{ approve, reject, restore }">
          <div class="tinder-like-actions">
            <CustomButton
              icon="pi pi-times"
              class="custom-button-sizes"
              severity="warn"
              rounded
              :disabled="isSubmittingAction"
              @click="reject"
            />
            <CustomButton
              icon="pi pi-heart-fill"
              class="custom-button-sizes"
              severity="warn"
              rounded
              :disabled="isSubmittingAction"
              @click="approve"
            />
          </div>
        </template>
      </FlashCards>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <p class="error-message">{{ error }}</p>
        <CustomButton @click="loadNextBatch" label="Попробовать еще раз" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useLookModal } from '@/features/look/store'
import type { LookStep, TinderCardItem } from '@/features/look/types/look-config'
import { fetchNextLookCard, submitLookCardAction } from '@/features/look/api/look'

import { FlashCards } from '@/components/HelpersComponents/Cards'
import TinderLikeCard from '@/components/HelpersComponents/Cards/components/TinderLikeCard.vue'
import CustomButton from '@/components/UI/CustomButtom/CustomButton.vue'

interface Props {
  step: LookStep & { type: 'tinder_like' }
}

const props = defineProps<Props>()
const lookStore = useLookModal()

// Константы
const BATCH_SIZE = 5
const MIN_CARDS_BEFORE_LOAD = 2

// Состояние
const cards = ref<Array<{ id: number; text: string; description: string; image: string; originalId?: string }>>([])
const processedCardsCount = ref(0)
const loadedCardsCount = ref(0)
const isLoadingBatch = ref(false)
const isSubmittingAction = ref(false)
const error = ref<string | null>(null)

const cardsCount = computed(() => props.step.cardsCount)
const lookConfigId = computed(() => lookStore.lookModal.config?.id || '')

function adaptCard(card: TinderCardItem): { id: number; text: string; description: string; image: string; originalId: string } {
  const numericId = parseInt(card.id, 10) || 0
  
  return {
    id: numericId,
    text: card.text,
    description: card.subtitle,
    image: card.image,
    originalId: card.id
  }
}

async function loadNextBatch() {
  if (!lookConfigId.value) {
    error.value = 'Конфигурация опроса не загружена'
    return
  }

  if (loadedCardsCount.value >= cardsCount.value) {
    return
  }

  const remainingCards = cardsCount.value - loadedCardsCount.value
  const batchToLoad = Math.min(BATCH_SIZE, remainingCards)

  isLoadingBatch.value = true
  error.value = null

  try {
    const newCards = await fetchNextLookCard({
      lookConfigId: lookConfigId.value,
      currentCardIndex: loadedCardsCount.value,
      limit: batchToLoad
    })

    for (const card of newCards) {
      if (!card.id) {
        throw new Error('Карточка не содержит обязательный id')
      }
    }

    const adaptedNewCards = newCards.map(adaptCard)
    cards.value.push(...adaptedNewCards)
    loadedCardsCount.value += newCards.length

    const stepKey = `${props.step.id}_cardIndex`
    lookStore.updateLookData(stepKey, processedCardsCount.value)
  } catch (err) {
    console.error('Error loading batch:', err)
    error.value = err instanceof Error ? err.message : 'Не удалось загрузить образы'
  } finally {
    isLoadingBatch.value = false
  }
}

function checkAndLoadNextBatch() {
  const remainingInArray = cards.value.length - processedCardsCount.value
  
  if (remainingInArray <= MIN_CARDS_BEFORE_LOAD && loadedCardsCount.value < cardsCount.value) {
    loadNextBatch()
  }
}

async function checkCompletion() {
  await nextTick()
  
  if (processedCardsCount.value >= cardsCount.value) {
    if (props.step.autoNext) {
      lookStore.nextStep()
    }
  }
}

async function handleCardAction(card: { id: number; text?: string; description?: string; image?: string; originalId?: string }, action: 'like' | 'dislike') {
  if (!lookConfigId.value) {
    console.error('[TinderLikeStep] Config id is missing')
    return
  }

  const cardId = card.originalId || card.id.toString()

  isSubmittingAction.value = true
  error.value = null

  try {
    await submitLookCardAction({
      lookConfigId: lookConfigId.value,
      cardId,
      action
    })

    processedCardsCount.value++
    
    const stepKey = `${props.step.id}_cardIndex`
    lookStore.updateLookData(stepKey, processedCardsCount.value)

    checkAndLoadNextBatch()
    await checkCompletion()
  } catch (err) {
    console.error('[TinderLikeStep] Error submitting card action:', err)
    const isMockMode = lookConfigId.value.includes('mock')
    
    if (isMockMode) {
      processedCardsCount.value++
      const stepKey = `${props.step.id}_cardIndex`
      lookStore.updateLookData(stepKey, processedCardsCount.value)
      checkAndLoadNextBatch()
      await checkCompletion()
    } else {
      error.value = err instanceof Error ? err.message : 'Не удалось отправить действие. Попробуйте еще раз.'
    }
  } finally {
    isSubmittingAction.value = false
  }
}

function onApprove(card: { id?: number; text?: string; description?: string; image?: string; originalId?: string }) {
  if (card.id === undefined || card.id === null) {
    console.warn('[TinderLikeStep] Card id is missing in onApprove')
    return
  }
  handleCardAction(card as { id: number; text?: string; description?: string; image?: string; originalId?: string }, 'like')
}

function onReject(card: { id?: number; text?: string; description?: string; image?: string; originalId?: string }) {
  if (card.id === undefined || card.id === null) {
    console.warn('[TinderLikeStep] Card id is missing in onReject')
    return
  }
  handleCardAction(card as { id: number; text?: string; description?: string; image?: string; originalId?: string }, 'dislike')
}

function initializeStep() {
  const stepKey = `${props.step.id}_cardIndex`
  const savedIndex = lookStore.lookModal.lookData[stepKey]
  
  if (typeof savedIndex === 'number' && savedIndex >= 0) {
    processedCardsCount.value = savedIndex
    loadedCardsCount.value = Math.ceil(savedIndex / BATCH_SIZE) * BATCH_SIZE
  } else {
    processedCardsCount.value = 0
    loadedCardsCount.value = 0
  }
  
  cards.value = []
  error.value = null
  isLoadingBatch.value = false
  isSubmittingAction.value = false
  
  loadNextBatch()
}

watch(processedCardsCount, async (newCount) => {
  const stepKey = `${props.step.id}_cardIndex`
  lookStore.updateLookData(stepKey, newCount)
  
  await nextTick()
  if (newCount >= cardsCount.value && props.step.autoNext) {
    lookStore.nextStep()
  }
})

onMounted(() => {
  initializeStep()
})

watch(() => props.step.id, () => {
  initializeStep()
}, { immediate: false })
</script>

<style scoped>
.tinder-like-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.tinder-survey-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.title-section {
  text-align: left;
  margin-bottom: 20px;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.full-height {
  flex: 1;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-container p {
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
}

.error-message {
  color: #ff3b30;
  font-size: 16px;
  margin: 0;
}

.tinder-like-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: absolute;
  bottom: 0;
  padding: 12px;
}

.custom-button-sizes {
  width: 60px;
  height: 60px;
}

.cards {
  position: relative;
}

@media (min-width: 393px) and (max-width: 767px) {
  .tinder-like-step {
    padding: 0;
  }

  .tinder-survey-content {
    width: 100%;
    padding: 30px 20px 0px 20px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: #232F45;
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .cards {
    width: 100%;
    max-width: 100%;
    align-self: stretch;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .tinder-like-step {
    padding: 30px 0 0 0;
    align-self: stretch;
  }

  .tinder-survey-content {
    width: 100%;
    padding: 0 20px 20px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;
  }

  .step-title {
    font-size: 32px;
    line-height: 1.09;
    font-weight: 700;
    color: #232F45;
    text-align: left;
    margin: 0;
  }

  .step-subtitle {
    font-size: 15px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .cards {
    width: 394px;
    max-width: 394px;
    align-self: center;
  }
}

@media (min-width: 1024px) and (max-width: 1439px) {
  .tinder-like-step {
    padding: 0;
  }

  .tinder-survey-content {
    width: 100%;
    padding: 40px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .cards {
    width: 394px;
    max-width: 394px;
    align-self: center;
  }
}

@media (min-width: 1440px) and (max-width: 1919px) {
  .tinder-like-step {
    padding: 0;
  }

  .tinder-survey-content {
    width: 100%;
    padding: 40px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .cards {
    width: 394px;
    max-width: 394px;
    align-self: center;
  }
}

@media (min-width: 1920px) {
  .tinder-like-step {
    padding: 0;
  }

  .tinder-survey-content {
    width: 100%;
    padding: 40px 120px;
    gap: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .title-section {
    margin-bottom: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    align-self: stretch;
  }

  .step-title {
    font-size: 48px;
    line-height: 1.2;
    font-weight: 700;
    color: #232F45;
    text-align: center;
    margin: 0;
  }

  .step-subtitle {
    font-size: 20px;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
  }

  .cards {
    width: 394px;
    max-width: 394px;
    align-self: center;
  }
}
</style>

